import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import SearchableSelect from '@/components/SearchableSelect';
import { Plus, AlertTriangle, Undo2, Pencil, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { trPara } from '@/lib/sayi';
import { kelimeleriIceriyor } from '@/lib/arama';

// ÇEK VE SENET (sunucu: routes/cek_senet.py). Çek bir HESAPTA durur — Portföy,
// Tahsilde, Teminatta, Verilen Çekler… — ve her işlem iki hesap arasında bir
// cari hareketi yazar. Durum SAKLANMAZ, hareketlerden türetilir; ekran izinli
// işlemleri sunucudan alır (`izinli_islemler`), kendi kuralını yazmaz.

const TUR_SECENEK = [
  { tur: 'musteri_ceki', ad: 'Müşteri çeki', aciklama: 'Bir cariden aldığımız çek' },
  { tur: 'kendi_cekimiz', ad: 'Kendi çekimiz', aciklama: 'Bizim keşide edip verdiğimiz çek' },
  { tur: 'alacak_senedi', ad: 'Alacak senedi', aciklama: 'Bir cariden aldığımız senet' },
  { tur: 'borc_senedi', ad: 'Borç senedi', aciklama: 'Bizim imzalayıp verdiğimiz senet' },
];
const ALINAN = new Set(['musteri_ceki', 'alacak_senedi']);
const KAPALI = new Set(['tahsil_edildi', 'odendi', 'iade_edildi', 'iade_alindi', 'teminat_iade', 'ciro', 'ciro_odendi']);

const KONUM_RENK = {
  portfoy: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', tahsilde: 'bg-[#2e5a7a]/10 text-[#2e5a7a]',
  teminatta: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', ciro: 'bg-[#e8dfd0] text-[#3F3E39]',
  tahsil_edildi: 'bg-[#e8dfd0] text-[#8a7e70]', odendi: 'bg-[#e8dfd0] text-[#8a7e70]',
  karsiliksiz: 'bg-[#7a3535]/10 text-[#7a3535]', odenecek: 'bg-[#7a3535]/10 text-[#7a3535]',
  iade_edildi: 'bg-[#e8dfd0] text-[#8a7e70]', iade_alindi: 'bg-[#e8dfd0] text-[#8a7e70]',
  teminat_elde: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', teminat_verildi: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  teminat_iade: 'bg-[#e8dfd0] text-[#8a7e70]', ciro_odendi: 'bg-[#e8dfd0] text-[#8a7e70]',
};

const OZET = [
  { k: 'portfoy', ad: 'Portföyde', not: 'Elimizdeki müşteri çek ve senetleri' },
  { k: 'tahsilde', ad: 'Bankada tahsilde', not: 'Takasa verilmiş, tahsil bekleyen' },
  { k: 'teminatta', ad: 'Teminatta', not: 'Teminata verilmiş müşteri çekleri' },
  { k: 'ciro_riski', ad: 'Ciro riski', not: 'Ciro ettiğimiz, vadesi gelmemiş çekler' },
  { k: 'odenecek', ad: 'Ödenecek', not: 'Verdiğimiz çek ve senetler' },
  { k: 'karsiliksiz', ad: 'Karşılıksız', not: 'Karşılıksız çıkan, cariye yazılan' },
];

const bugun = () => new Date().toISOString().slice(0, 10);
const tarihTR = (t) => (t ? new Date(t + 'T00:00:00').toLocaleDateString('tr-TR') : '—');
const tutarlar = (pb) => Object.entries(pb || {}).map(([p, v]) => `${trPara(v)} ${p}`).join(' · ') || '—';

const BOS_FORM = {
  tur: 'musteri_ceki', teminat: false, cari_id: '', tarih: bugun(), vade: '', tutar: '',
  para_birimi: 'TRY', no: '', banka: '', sube: '', hesap_no: '', kesideci: '', not: '',
};

export default function CekSenet({ hesaplar = [], kasaBanka = [], silmeYetkisi = false, onDegisti }) {
  const [veri, setVeri] = useState({ kartlar: [], ozet: {} });
  const [yukleniyor, setYukleniyor] = useState(true);
  const [suzgec, setSuzgec] = useState({ tur: 'hepsi', durum: 'acik', ara: '' });
  const [yeni, setYeni] = useState(null);       // form
  const [detay, setDetay] = useState(null);     // kart
  const [islem, setIslem] = useState(null);     // {kart, islem, ad, ister, tarih, cari_id, hesap_id, not}
  const [duzen, setDuzen] = useState(null);     // kart bilgisi düzenleme
  const [harDuzen, setHarDuzen] = useState(null); // {kart, i, tarih, cari_id, hesap_id, not}
  const [toplu, setToplu] = useState(null);       // Set(kart id) — vadesi geçen ciroların ödendi kapanışı
  const [bekle, setBekle] = useState(false);

  const yukle = useCallback(async () => {
    try { const r = await api.get('/treasury/cek-senet'); setVeri(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Çek ve senetler okunamadı'); }
    setYukleniyor(false);
  }, []);
  useEffect(() => { yukle(); }, [yukle]);

  // Karşı taraf: çek/senet hesapları hariç her hesap (cari, ortak, personel)
  const cariSecenek = useMemo(() => hesaplar
    .filter(h => h.account_type !== 'Çek ve Senetler')
    .map(h => ({ value: h.id, label: h.name, hint: h.code || '', group: h.account_type || 'Diğer' })), [hesaplar]);
  const kasaSecenek = useMemo(() => kasaBanka.map(h => ({ value: h.id, label: h.name, hint: h.currency || '' })), [kasaBanka]);

  const gorunen = useMemo(() => veri.kartlar.filter(k => {
    if (suzgec.tur !== 'hepsi' && k.tur !== suzgec.tur) return false;
    if (suzgec.durum === 'acik' && KAPALI.has(k.konum)) {
      // Ciro edilmiş çek vadesi gelene kadar risk taşır, açıklarda kalır
      if (!(k.konum === 'ciro' && (k.vadeye_gun ?? 0) >= 0)) return false;
    }
    if (suzgec.durum === 'kapali' && !KAPALI.has(k.konum)) return false;
    if (suzgec.ara && !kelimeleriIceriyor(suzgec.ara,
      `${k.no} ${k.banka} ${k.kesideci} ${k.ilk_cari_ad} ${k.ciro_cari_ad} ${k.tutar}`)) return false;
    return true;
  }), [veri.kartlar, suzgec]);

  const kartiTazele = (k) => {
    setVeri(v => ({ ...v, kartlar: v.kartlar.map(x => x.id === k.id ? k : x) }));
    if (detay?.id === k.id) setDetay(k);
  };

  const kaydetYeni = async () => {
    setBekle(true);
    try {
      const r = await api.post('/treasury/cek-senet', { ...yeni, tutar: parseFloat(String(yeni.tutar).replace(/\./g, '').replace(',', '.')) });
      toast.success(`${r.data.tur_ad} kaydedildi`);
      setYeni(null); await yukle(); onDegisti?.(); setDetay(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    setBekle(false);
  };

  const islemYap = async () => {
    setBekle(true);
    try {
      const r = await api.post(`/treasury/cek-senet/${islem.kart.id}/islem`, {
        islem: islem.islem, tarih: islem.tarih, cari_id: islem.cari_id || undefined,
        hesap_id: islem.hesap_id || undefined, not: islem.not });
      toast.success(`${islem.ad}: kaydedildi`);
      setIslem(null); await yukle(); onDegisti?.(); setDetay(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'İşlem yapılamadı'); }
    setBekle(false);
  };

  const geriAl = async (k) => {
    setBekle(true);
    try {
      const r = await api.delete(`/treasury/cek-senet/${k.id}/son-islem`);
      toast.success(r.data.silindi ? 'Kayıt silindi' : 'Son işlem geri alındı');
      setDetay(r.data.silindi ? null : r.data); await yukle(); onDegisti?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Geri alınamadı'); }
    setBekle(false);
  };

  const kaydetDuzen = async () => {
    setBekle(true);
    try {
      const { id, no, banka, sube, hesap_no, kesideci, vade, not, para_birimi } = duzen;
      const tutar = parseFloat(String(duzen.tutar).replace(/\./g, '').replace(',', '.'));
      const r = await api.patch(`/treasury/cek-senet/${id}`, { no, banka, sube, hesap_no, kesideci, vade, not, tutar, para_birimi });
      await yukle(); onDegisti?.();
      toast.success('Bilgiler güncellendi'); setDuzen(null); kartiTazele(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    setBekle(false);
  };

  const kaydetHareket = async () => {
    setBekle(true);
    try {
      const { kart, i, tarih, cari_id, hesap_id, not } = harDuzen;
      const r = await api.patch(`/treasury/cek-senet/${kart.id}/hareket/${kart.hareketler[i].id}`,
        { tarih, not, cari_id: cari_id || undefined, hesap_id: hesap_id || undefined });
      toast.success('Hareket güncellendi'); setHarDuzen(null); kartiTazele(r.data); await yukle(); onDegisti?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    setBekle(false);
  };

  // Vadesi geçmiş, ödendi diye kapatılmamış ciro edilmiş çekler
  const vadesiGecenCiro = useMemo(() => veri.kartlar.filter(k => k.konum === 'ciro' && k.vadeye_gun < 0), [veri.kartlar]);

  const kaydetToplu = async () => {
    setBekle(true);
    try {
      const r = await api.post('/treasury/cek-senet/toplu-islem', { islem: 'ciro_odendi', kart_ids: [...toplu] });
      if (r.data.kalan?.length) toast.warning(`${r.data.islenen} çek kapatıldı; ${r.data.kalan.length} çek kalmadı: ${r.data.kalan.map(k => `${k.no || '—'} (${k.neden})`).join('; ')}`);
      else toast.success(`${r.data.islenen} çek ödendi olarak kapatıldı`);
      setToplu(null); await yukle();
    } catch (e) { toast.error(e.response?.data?.detail || 'İşlenemedi'); }
    setBekle(false);
  };

  const islemAc = (k, i) => {
    // Bankadaki çekin tahsilinde para, takasa verildiği bankaya girer
    const takas = [...(k.hareketler || [])].reverse().find(h => h.islem === 'tahsile_ver');
    // Vadeye bağlı işlemlerin varsayılan tarihi vade; son işlemden önce olamaz (sunucu reddeder)
    const son = k.hareketler?.at(-1)?.tarih || '';
    const vadeli = ['tahsil', 'odeme', 'ciro_odendi'].includes(i.islem);
    setIslem({ kart: k, ...i, tarih: vadeli ? ((k.vade || bugun()) < son ? son : (k.vade || bugun())) : (bugun() < son ? son : bugun()),
      cari_id: '', hesap_id: i.islem === 'tahsil' && k.konum === 'tahsilde' ? (takas?.hesap_id || '') : '', not: '' });
  };

  const alinan = yeni && ALINAN.has(yeni.tur);

  return (
    <div className="space-y-4" data-testid="cek-senet">
      {/* ── Özet: hangi hesapta ne var ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {OZET.map(o => (
          <button key={o.k} type="button" title={o.not}
            onClick={() => setSuzgec(s => ({ ...s, durum: o.k === 'ciro_riski' ? 'ciro' : o.k }))}
            className={`text-left rounded-lg border border-[#d0c4b0] bg-[#faf7f2] px-3 py-2.5 hover:border-[#3d6e4e] ${suzgec.durum === (o.k === 'ciro_riski' ? 'ciro' : o.k) ? 'ring-1 ring-[#3d6e4e]' : ''}`}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#8a7e70]">{o.ad}
              {veri.ozet?._adet?.[o.k] ? <span className="ml-1 font-mono">({veri.ozet._adet[o.k]})</span> : null}</p>
            <p className="font-mono text-sm font-semibold text-[#1a1916] mt-1 tabular-nums">{tutarlar(veri.ozet?.[o.k])}</p>
          </button>
        ))}
      </div>

      {vadesiGecenCiro.length > 0 && (
        <div className="rounded-lg border border-[#c9b98a] bg-[#7a6a2e]/10 px-4 py-3 text-sm text-[#3F3E39] flex flex-wrap items-center justify-between gap-3">
          <span><b>{vadesiGecenCiro.length} ciro edilmiş çekin vadesi geçti.</b> Karşılıksız dönmediyse ödendi olarak kapatın; bu işlem cari bakiyesine dokunmaz, yalnızca ciro riskini kapatır.</span>
          <Button size="sm" onClick={() => setToplu(new Set(vadesiGecenCiro.map(k => k.id)))} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Ödendi işaretle…</Button>
        </div>
      )}

      {/* ── Süzgeç ── */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-xs text-[#8a7e70]">Tür</Label>
          <Select value={suzgec.tur} onValueChange={v => setSuzgec(s => ({ ...s, tur: v }))}>
            <SelectTrigger className="w-44 bg-white border-[#d0c4b0] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hepsi">Tüm türler</SelectItem>
              {TUR_SECENEK.map(t => <SelectItem key={t.tur} value={t.tur}>{t.ad}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-[#8a7e70]">Durum</Label>
          <Select value={suzgec.durum} onValueChange={v => setSuzgec(s => ({ ...s, durum: v }))}>
            <SelectTrigger className="w-48 bg-white border-[#d0c4b0] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="acik">Açık olanlar</SelectItem>
              <SelectItem value="hepsi">Tümü</SelectItem>
              <SelectItem value="kapali">Kapananlar</SelectItem>
              {Object.entries(veri.konumlar || {}).map(([k, ad]) => <SelectItem key={k} value={k}>{ad}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 flex-1 min-w-[180px]">
          <Label className="text-xs text-[#8a7e70]">Ara</Label>
          <Input value={suzgec.ara} onChange={e => setSuzgec(s => ({ ...s, ara: e.target.value }))}
            placeholder="No, banka, keşideci, cari, tutar" className="bg-white border-[#d0c4b0] h-9" />
        </div>
        <Button size="sm" onClick={() => setYeni({ ...BOS_FORM })} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] h-9">
          <Plus size={14} className="mr-1" /> Yeni Çek / Senet
        </Button>
      </div>

      {/* ── Liste ── */}
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            {['Tür', 'No / Banka', 'Kimden / Kime', 'Vade', 'Tutar', 'Durum'].map((b, i) => (
              <TableHead key={b} className={`text-[#1a1916] font-bold text-xs uppercase ${i === 4 ? 'text-right' : ''}`}>{b}</TableHead>))}
          </TableRow></TableHeader>
          <TableBody>
            {gorunen.map(k => {
              const gecti = !KAPALI.has(k.konum) && k.vadeye_gun < 0;
              return (
                <TableRow key={k.id} onClick={() => setDetay(k)}
                  className={`border-[#d0c4b0] cursor-pointer hover:bg-[#f0e9dc] ${gecti ? 'bg-[#7a3535]/5' : ''}`}>
                  <TableCell className="text-sm whitespace-nowrap">{k.tur_ad}</TableCell>
                  <TableCell className="text-sm"><span className="font-mono">{k.no || '—'}</span>
                    {k.banka && <span className="block text-xs text-[#8a7e70]">{k.banka}{k.sube ? ` / ${k.sube}` : ''}</span>}</TableCell>
                  <TableCell className="text-sm">
                    {k.ilk_cari_ad}{k.ciro_cari_ad && <span className="text-[#8a7e70]"> <ArrowRight size={11} className="inline" /> {k.ciro_cari_ad}</span>}
                    {ALINAN.has(k.tur) && k.kesideci && k.kesideci !== k.ilk_cari_ad && <span className="block text-xs text-[#8a7e70]">Keşideci: {k.kesideci}</span>}
                  </TableCell>
                  <TableCell className={`text-sm whitespace-nowrap ${gecti ? 'text-[#7a3535] font-semibold' : ''}`}>
                    {tarihTR(k.vade)}
                    {!KAPALI.has(k.konum) && k.vadeye_gun != null && <span className="text-xs ml-1">({k.vadeye_gun >= 0 ? `${k.vadeye_gun} gün` : `${-k.vadeye_gun} gün geçti`})</span>}
                    {gecti && <AlertTriangle size={12} className="inline ml-1" />}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold text-[#1a1916] whitespace-nowrap">{trPara(k.tutar)} {k.para_birimi}</TableCell>
                  <TableCell><span className={`status-badge ${KONUM_RENK[k.konum] || ''}`}>{k.konum_ad}</span></TableCell>
                </TableRow>);
            })}
            {!gorunen.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">
              {yukleniyor ? 'Yükleniyor…' : veri.kartlar.length ? 'Süzgece uyan çek / senet yok' : 'Henüz çek ya da senet yok. "Yeni Çek / Senet" ile ilkini girin.'}
            </TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {veri.kartlar.length > 0 && <p className="text-xs text-[#8a7e70]">
        {gorunen.length} / {veri.kartlar.length} kayıt
        {gorunen.length < veri.kartlar.length && <> · {veri.kartlar.length - gorunen.length} kayıt süzgeç yüzünden gizli
          {suzgec.durum === 'acik' && ' (tahsil edilmiş, ödenmiş, iade edilmiş, ödendi diye kapatılmış ya da vadesi geçmiş ciro edilmiş çekler)'}
          {' '}<button type="button" className="underline text-[#3d6e4e]" onClick={() => setSuzgec({ tur: 'hepsi', durum: 'hepsi', ara: '' })}>tümünü göster</button></>}
        {gorunen.length > 0 && ' · satıra tıklayınca işlemler açılır'}</p>}

      {/* ── Yeni ── */}
      <Dialog open={!!yeni} onOpenChange={a => !a && setYeni(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Çek / Senet</DialogTitle>
            <DialogDescription>Kayıtla birlikte giriş hareketi yazılır: alınan evrak çeki verenden portföye, verilen evrak cariye ve "Ödenecek" hesabına.</DialogDescription></DialogHeader>
          {yeni && <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {TUR_SECENEK.map(t => (
                <button key={t.tur} type="button" onClick={() => setYeni(f => ({ ...f, tur: t.tur }))}
                  className={`text-left rounded-md border px-3 py-2 ${yeni.tur === t.tur ? 'border-[#3d6e4e] bg-[#3d6e4e]/10' : 'border-[#d0c4b0] bg-white'}`}>
                  <span className="block text-sm font-semibold text-[#1a1916]">{t.ad}</span>
                  <span className="block text-xs text-[#8a7e70]">{t.aciklama}</span>
                </button>))}
            </div>
            <label className="flex items-start gap-2 text-sm text-[#3F3E39]">
              <input type="checkbox" className="mt-0.5" checked={yeni.teminat} onChange={e => setYeni(f => ({ ...f, teminat: e.target.checked }))} />
              <span>Teminat {alinan ? 'olarak alındı' : 'olarak verildi'} — borç / alacak doğurmaz, yalnızca takip edilir</span>
            </label>
            <div className="space-y-1.5">
              <Label>{alinan ? 'Kimden alındı' : 'Kime verildi'}</Label>
              <SearchableSelect value={yeni.cari_id} options={cariSecenek} onChange={v => {
                const c = hesaplar.find(h => h.id === v);
                setYeni(f => ({ ...f, cari_id: v, kesideci: alinan && !f.kesideci ? (c?.name || '') : f.kesideci }));
              }} placeholder="Cari seçin" searchPlaceholder="Cari ara…" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5"><Label>{alinan ? 'Alış tarihi' : 'Veriliş tarihi'}</Label>
                <Input type="date" value={yeni.tarih} onChange={e => setYeni(f => ({ ...f, tarih: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-1.5"><Label>Vade</Label>
                <Input type="date" value={yeni.vade} onChange={e => setYeni(f => ({ ...f, vade: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-1.5"><Label>Tutar</Label>
                <Input inputMode="decimal" value={yeni.tutar} onChange={e => setYeni(f => ({ ...f, tutar: e.target.value }))} placeholder="323.993,15" className="bg-white border-[#d0c4b0] font-mono" /></div>
              <div className="space-y-1.5"><Label>Para birimi</Label>
                <Select value={yeni.para_birimi} onValueChange={v => setYeni(f => ({ ...f, para_birimi: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{['TRY', 'EUR', 'USD', 'GBP'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5"><Label>{yeni.tur.endsWith('senedi') ? 'Senet no' : 'Çek no'}</Label>
                <Input value={yeni.no} onChange={e => setYeni(f => ({ ...f, no: e.target.value }))} className="bg-white border-[#d0c4b0] font-mono" /></div>
              {!yeni.tur.endsWith('senedi') && <>
                <div className="space-y-1.5"><Label>Banka</Label>
                  <Input value={yeni.banka} onChange={e => setYeni(f => ({ ...f, banka: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
                <div className="space-y-1.5"><Label>Şube</Label>
                  <Input value={yeni.sube} onChange={e => setYeni(f => ({ ...f, sube: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
                <div className="space-y-1.5"><Label>Hesap no</Label>
                  <Input value={yeni.hesap_no} onChange={e => setYeni(f => ({ ...f, hesap_no: e.target.value }))} className="bg-white border-[#d0c4b0] font-mono" /></div>
              </>}
            </div>
            {alinan && <div className="space-y-1.5"><Label>Keşideci (asıl borçlu)</Label>
              <Input value={yeni.kesideci} onChange={e => setYeni(f => ({ ...f, kesideci: e.target.value }))}
                placeholder="Çeki yazan; cariden farklıysa (ciro ile gelen çek)" className="bg-white border-[#d0c4b0]" /></div>}
            <div className="space-y-1.5"><Label>Not</Label>
              <Textarea value={yeni.not} onChange={e => setYeni(f => ({ ...f, not: e.target.value }))} rows={2} className="bg-white border-[#d0c4b0]" /></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setYeni(null)} className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={kaydetYeni} disabled={bekle || !yeni?.cari_id || !yeni?.vade || !yeni?.tutar}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">{bekle && <Loader2 size={14} className="mr-1 animate-spin" />}Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detay ── */}
      <Dialog open={!!detay && !islem && !duzen && !harDuzen} onOpenChange={a => !a && setDetay(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-2xl max-h-[90vh] overflow-y-auto">
          {detay && <>
            <DialogHeader>
              <DialogTitle className="text-[#1a1916] font-bold flex flex-wrap items-center gap-2">
                {detay.tur_ad} <span className="font-mono">{detay.no || ''}</span>
                <span className={`status-badge ${KONUM_RENK[detay.konum] || ''}`}>{detay.konum_ad}</span>
              </DialogTitle>
              <DialogDescription>
                <span className="font-mono font-semibold text-[#1a1916]">{trPara(detay.tutar)} {detay.para_birimi}</span>
                {' · '}vade {tarihTR(detay.vade)}{detay.banka ? ` · ${detay.banka}${detay.sube ? ' / ' + detay.sube : ''}` : ''}
                {detay.kesideci ? ` · keşideci ${detay.kesideci}` : ''}
              </DialogDescription>
            </DialogHeader>
            {detay.izinli_islemler?.length > 0 && <div className="flex flex-wrap gap-2">
              {detay.izinli_islemler.map(i => (
                <Button key={i.islem} size="sm" variant={i.islem === 'karsiliksiz' ? 'outline' : 'default'}
                  onClick={() => islemAc(detay, i)}
                  className={i.islem === 'karsiliksiz' ? 'border-[#7a3535] text-[#7a3535]' : 'bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]'}>{i.ad}</Button>))}
            </div>}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70] mb-2">Hareketler</p>
              <ol className="space-y-2">
                {detay.hareketler.map((h, n) => (
                  <li key={h.id} className="rounded-md border border-[#d0c4b0] bg-white px-3 py-2 text-sm">
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="font-semibold text-[#1a1916]">{n === 0 ? (ALINAN.has(detay.tur) ? 'Alındı' : 'Verildi') : (ISLEM_AD[h.islem] || h.islem)}</span>
                      <span className="flex items-center gap-2 text-[#8a7e70]">{tarihTR(h.tarih)}
                        <button type="button" aria-label="Hareketi düzenle" title="Tarih, karşı taraf, not"
                          onClick={() => setHarDuzen({ kart: detay, i: n, tarih: h.tarih, cari_id: h.cari_id || '', hesap_id: h.hesap_id || '', not: h.not || '' })}
                          className="p-1 rounded hover:bg-[#e8dfd0] text-[#3F3E39]"><Pencil size={13} /></button></span>
                    </div>
                    <div className="text-[#3F3E39]">
                      {h.cari_ad && <Link to={`/erp/suppliers/${h.cari_id}`} className="underline">{h.cari_ad}</Link>}
                      {h.hesap_ad && <> {h.cari_ad ? ' · ' : ''}<Link to={`/erp/suppliers/${h.hesap_id}`} className="underline">{h.hesap_ad}</Link></>}
                      {!h.ledger_id && <span className="text-xs text-[#8a7e70]"> · cari hareketi yok (teminat)</span>}
                      {h.not && <span className="block text-xs text-[#8a7e70]">{h.not}</span>}
                    </div>
                  </li>))}
              </ol>
            </div>
            <DialogFooter className="flex-wrap gap-2 sm:justify-between">
              <Button variant="outline" size="sm" onClick={() => setDuzen({ ...detay, tutar: String(detay.tutar).replace('.', ',') })} className="border-[#d0c4b0]"><Pencil size={13} className="mr-1" /> Bilgileri düzenle</Button>
              {silmeYetkisi && <Button variant="outline" size="sm" disabled={bekle} onClick={() => geriAl(detay)} className="border-[#7a3535] text-[#7a3535]">
                <Undo2 size={13} className="mr-1" /> {detay.hareketler.length === 1 ? 'Kaydı sil' : `Son işlemi geri al (${ISLEM_AD[detay.hareketler.at(-1).islem] || ''})`}</Button>}
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* ── İşlem ── */}
      <Dialog open={!!islem} onOpenChange={a => !a && setIslem(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          {islem && <>
            <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">{islem.ad}</DialogTitle>
              <DialogDescription>{islem.kart.tur_ad} {islem.kart.no} · {trPara(islem.kart.tutar)} {islem.kart.para_birimi} · vade {tarihTR(islem.kart.vade)}
                <span className="block mt-1">{ISLEM_ACIKLAMA[islem.islem]}</span></DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>İşlem tarihi</Label>
                <Input type="date" value={islem.tarih} onChange={e => setIslem(s => ({ ...s, tarih: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              {islem.ister.includes('cari') && <div className="space-y-1.5"><Label>Kime</Label>
                <SearchableSelect value={islem.cari_id} options={cariSecenek} onChange={v => setIslem(s => ({ ...s, cari_id: v }))} placeholder="Cari seçin" searchPlaceholder="Cari ara…" /></div>}
              {(islem.ister.includes('hesap')) && <div className="space-y-1.5">
                <Label>{islem.islem === 'tahsile_ver' ? 'Hangi bankaya' : islem.islem === 'odeme' ? 'Hangi hesaptan ödendi' : 'Hangi hesaba girdi'}</Label>
                <SearchableSelect value={islem.hesap_id} options={kasaSecenek} onChange={v => setIslem(s => ({ ...s, hesap_id: v }))} placeholder="Kasa / banka seçin" /></div>}
              <div className="space-y-1.5"><Label>Not</Label>
                <Textarea rows={2} value={islem.not} onChange={e => setIslem(s => ({ ...s, not: e.target.value }))} className="bg-white border-[#d0c4b0]"
                  placeholder={islem.islem === 'teminata_ver' ? 'Kime / ne için teminat verildi' : ''} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIslem(null)} className="border-[#d0c4b0]">Vazgeç</Button>
              <Button onClick={islemYap} disabled={bekle || (islem.ister.includes('cari') && !islem.cari_id) || (islem.ister.includes('hesap') && !islem.hesap_id)}
                className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">{bekle && <Loader2 size={14} className="mr-1 animate-spin" />}Kaydet</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* ── Toplu ödendi kapanışı ── */}
      <Dialog open={!!toplu} onOpenChange={a => !a && setToplu(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-2xl max-h-[90vh] overflow-y-auto">
          {toplu && <>
            <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Vadesi geçen ciro edilmiş çekler — ödendi</DialogTitle>
              <DialogDescription>İşaretli çekler vade tarihiyle "ödendi" olarak kapanır. Cari hareketi yazılmaz. Karşılıksız dönen bir çek varsa işaretini kaldırın; o çekte "Ciro edilen firma geri verdi" işlemini yapın.</DialogDescription></DialogHeader>
            <div className="space-y-1.5">
              {vadesiGecenCiro.map(k => (
                <label key={k.id} className="flex items-center gap-3 rounded-md border border-[#d0c4b0] bg-white px-3 py-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={toplu.has(k.id)} onChange={e => setToplu(t => { const n = new Set(t); e.target.checked ? n.add(k.id) : n.delete(k.id); return n; })} />
                  <span className="font-mono w-20">{k.no || '—'}</span>
                  <span className="flex-1 min-w-0 truncate">{k.ciro_cari_ad || k.ilk_cari_ad}</span>
                  <span className="text-[#8a7e70] whitespace-nowrap">vade {tarihTR(k.vade)}</span>
                  <span className="font-mono font-semibold whitespace-nowrap">{trPara(k.tutar)} {k.para_birimi}</span>
                </label>))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setToplu(null)} className="border-[#d0c4b0]">Vazgeç</Button>
              <Button onClick={kaydetToplu} disabled={bekle || !toplu.size} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
                {bekle && <Loader2 size={14} className="mr-1 animate-spin" />}{toplu.size} çeki ödendi olarak kapat</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* ── Hareket düzenleme ── */}
      <Dialog open={!!harDuzen} onOpenChange={a => !a && setHarDuzen(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          {harDuzen && <HareketDuzenFormu harDuzen={harDuzen} setHarDuzen={setHarDuzen} cariSecenek={cariSecenek}
            kasaSecenek={kasaSecenek} bekle={bekle} onKaydet={kaydetHareket} />}
        </DialogContent>
      </Dialog>

      {/* ── Bilgi düzenleme ── */}
      <Dialog open={!!duzen} onOpenChange={a => !a && setDuzen(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          {duzen && <>
            <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Bilgileri düzenle</DialogTitle>
              <DialogDescription>Kaydedince çekin bütün cari hareketleri yeni tutar ve bilgilerle yeniden yazılır. Karşı cari ve işlem tarihleri hareketlerin yanındaki kalemle düzenlenir.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[['no', 'No'], ['banka', 'Banka'], ['sube', 'Şube'], ['hesap_no', 'Hesap no'], ['kesideci', 'Keşideci']].map(([a, ad]) => (
                <div key={a} className="space-y-1.5"><Label>{ad}</Label>
                  <Input value={duzen[a] || ''} onChange={e => setDuzen(d => ({ ...d, [a]: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>))}
              <div className="space-y-1.5"><Label>Vade</Label>
                <Input type="date" value={duzen.vade || ''} onChange={e => setDuzen(d => ({ ...d, vade: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-1.5"><Label>Tutar</Label>
                <Input inputMode="decimal" value={duzen.tutar ?? ''} onChange={e => setDuzen(d => ({ ...d, tutar: e.target.value }))} className="bg-white border-[#d0c4b0] font-mono" /></div>
              <div className="space-y-1.5"><Label>Para birimi</Label>
                <Select value={duzen.para_birimi} onValueChange={v => setDuzen(d => ({ ...d, para_birimi: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{['TRY', 'EUR', 'USD', 'GBP'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5 col-span-2"><Label>Not</Label>
                <Textarea rows={2} value={duzen.not || ''} onChange={e => setDuzen(d => ({ ...d, not: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuzen(null)} className="border-[#d0c4b0]">Vazgeç</Button>
              <Button onClick={kaydetDuzen} disabled={bekle} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Dosya düzeyinde: bileşen içinde tanımlanan bileşen her çizimde yeniden kurulur
// ve açık seçicinin durumu kaybolur (ERP.md "Bileşeni bileşenin İÇİNDE tanımlama").
function HareketDuzenFormu({ harDuzen, setHarDuzen, cariSecenek, kasaSecenek, bekle, onKaydet }) {
  const h = harDuzen.kart.hareketler[harDuzen.i];
  const cariVar = harDuzen.i === 0 || h.islem === 'ciro';
  const hesapVar = ['tahsile_ver', 'tahsil', 'odeme'].includes(h.islem);
  const alinan = ALINAN.has(harDuzen.kart.tur);
  const ad = harDuzen.i === 0 ? (alinan ? 'Alındı' : 'Verildi') : (ISLEM_AD[h.islem] || h.islem);
  return <>
    <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Hareketi düzenle — {ad}</DialogTitle>
      <DialogDescription>Kaydedince bu hareketin ve ona bağlı hareketlerin cari kayıtları yeniden yazılır
        {harDuzen.i === 0 ? ' (giriş carisi değişirse karşılıksız / iade kaydı da yeni cariye geçer)' : ''}.</DialogDescription></DialogHeader>
    <div className="space-y-3">
      <div className="space-y-1.5"><Label>Tarih</Label>
        <Input type="date" value={harDuzen.tarih} onChange={e => setHarDuzen(d => ({ ...d, tarih: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
      {cariVar && <div className="space-y-1.5"><Label>{harDuzen.i === 0 ? (alinan ? 'Kimden alındı' : 'Kime verildi') : 'Kime ciro edildi'}</Label>
        <SearchableSelect value={harDuzen.cari_id} options={cariSecenek} onChange={v => setHarDuzen(d => ({ ...d, cari_id: v }))} placeholder="Cari seçin" searchPlaceholder="Cari ara…" /></div>}
      {hesapVar && <div className="space-y-1.5"><Label>{h.islem === 'tahsile_ver' ? 'Hangi banka' : h.islem === 'odeme' ? 'Hangi hesaptan ödendi' : 'Hangi hesaba girdi'}</Label>
        <SearchableSelect value={harDuzen.hesap_id} options={kasaSecenek} onChange={v => setHarDuzen(d => ({ ...d, hesap_id: v }))} placeholder="Kasa / banka seçin" /></div>}
      <div className="space-y-1.5"><Label>Not</Label>
        <Textarea rows={2} value={harDuzen.not} onChange={e => setHarDuzen(d => ({ ...d, not: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
    </div>
    <DialogFooter>
      <Button variant="outline" onClick={() => setHarDuzen(null)} className="border-[#d0c4b0]">Vazgeç</Button>
      <Button onClick={onKaydet} disabled={bekle || (cariVar && !harDuzen.cari_id) || (hesapVar && !harDuzen.hesap_id)}
        className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">{bekle && <Loader2 size={14} className="mr-1 animate-spin" />}Kaydet</Button>
    </DialogFooter>
  </>;
}

const ISLEM_AD = {
  giris: 'Giriş', ciro: 'Ciro edildi', tahsile_ver: 'Bankaya takasa verildi', tahsilden_geri: 'Bankadan geri alındı',
  teminata_ver: 'Teminata verildi', teminattan_geri: 'Teminattan geri alındı', tahsil: 'Tahsil edildi',
  karsiliksiz: 'Karşılıksız çıktı', iade: 'Sahibine iade edildi', ciro_iade: 'Ciro edilen firma geri verdi',
  odeme: 'Ödendi', iade_alindi: 'Ödenmeden geri alındı', teminat_iade: 'Teminat iade edildi',
  ciro_odendi: 'Ödendi (ciroda)',
};

const ISLEM_ACIKLAMA = {
  ciro: 'Çek portföyden çıkar, seçtiğiniz cari alacaklanır (ona olan borcumuz düşer). Vadesine kadar ciro riski listesinde görünür.',
  tahsile_ver: 'Çek portföyden "Tahsilde" hesabına geçer. Tahsil edildiğinde para seçtiğiniz bankaya girer.',
  tahsilden_geri: 'Banka çeki tahsil etmeden geri verdi; çek portföye döner.',
  teminata_ver: 'Çek portföyden "Teminatta" hesabına geçer; cari bakiyeler değişmez.',
  teminattan_geri: 'Teminattaki çek geri alındı; portföye döner.',
  tahsil: 'Çek tahsil edildi; tutar seçtiğiniz kasa / banka hesabına girer.',
  karsiliksiz: 'Çek karşılıksız çıktı: tutar, çeki veren cariye yeniden borç olarak yazılır.',
  iade: 'Çek sahibine iade edildi: tutar, çeki veren cariye yeniden borç olarak yazılır.',
  ciro_iade: 'Ciro ettiğimiz firma çeki geri verdi: o firmaya yeniden borçlanırız, çek portföye döner. Karşılıksızsa ardından "Karşılıksız çıktı" işlenir.',
  odeme: 'Çekimiz vadesinde ödendi; tutar seçtiğiniz hesaptan çıkar, "Ödenecek" hesabı kapanır.',
  iade_alindi: 'Verdiğimiz çek ödenmeden geri alındı: o cariye yeniden borçlanırız.',
  teminat_iade: 'Teminat evrakı iade edildi / geri alındı. Cari hareketi yoktur.',
  ciro_odendi: 'Ciro ettiğimiz çek vadesinde ödendi: keşideci ödedi, ciro ettiğimiz firma tahsil etti. Bizde para hareketi olmaz, cari hareketi yazılmaz; yalnızca ciro riski kapanır.',
};
